import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useHasPermission from '@/hooks/useHasPermission';
import useUser from '@/hooks/useUser';
import { Permission, Role } from '@/types/resources';

const QuickLinks = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
`;

type DashboardLink = {
  to: string;
  title: string;
  permissions?: Permission[];
  freelancerOnly?: boolean;
  excludeRoles?: Role[];
};

const Dashboard = () => {
  const user = useUser()!;

  // Team Members don't review invoices, unless they've been given company /
  // department invoice access.
  const canReviewScopedInvoices = useHasPermission(
    'view companies invoices',
    'view departments invoices',
  );

  const links: DashboardLink[] = [
    {
      to: '/general/editorial-distribution-queue',
      permissions: ['use distribution queue'],
      title: 'Distribution Queue',
    },
    {
      to: '/general/invoices',
      title: 'Invoices',
      permissions: [
        'manage invoices',
        'view your invoices',
        'view companies invoices',
        'view departments invoices',
        'view all invoices',
        'add invoices',
      ],
    },
    {
      to: '/general/freelancers/type/employed',
      title: 'Freelancers',
      permissions: [
        'manage freelancers',
        'view all freelancers',
        'view your freelancers',
        'view companies freelancers',
        'view departments freelancers',
        'manage freelancer work email',
      ],
    },
    {
      to: '/general/cover-images/generate/one',
      title: 'Cover Images',
    },
    {
      to: '/general/users-management',
      title: 'Users',
      permissions: ['manage users'],
    },
    {
      to: '/general/freelancers/invoices',
      title: 'Generate Invoice',
      freelancerOnly: true,
    },
    {
      to: '/general/freelancers/request-vacation',
      title: 'Request Vacation',
      freelancerOnly: true,
    },
    {
      to: '/general/freelancers/vacation-approvals',
      title: 'Vacation Approvals',
      permissions: [
        'view your freelancers',
        'view companies vacations',
        'view departments vacations',
        'view all freelancers',
        'manage freelancers',
      ],
    },
    {
      to: '/general/freelancers/invoices/approvals',
      title: 'Invoice Approvals',
      ...(!canReviewScopedInvoices && { excludeRoles: ['Team Member'] }),
    },
  ];

  const filteredLinks = links.filter((link) => {
    if (link.freelancerOnly && !user.freelancer?.id) {
      return false;
    }

    if (
      !!link.permissions &&
      !user.permissions!.find((p) => link.permissions!.includes(p))
    ) {
      return false;
    }

    if (link.excludeRoles?.includes(user.role.name as Role)) {
      return false;
    }

    return true;
  });

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <PageHeading>Welcome back!</PageHeading>
      <QuickLinks>
        {filteredLinks.map((link, i) => (
          <Button to={link.to} key={i} as={Link} color="secondary">
            {link.title}
          </Button>
        ))}
      </QuickLinks>
    </>
  );
};

export default Dashboard;
